/**
 * 附件选择与读入（T09）。
 *
 * 流程：隐藏的 <input type="file"> 触发系统选择器 → 校验（validate.ts）→ FileReader 读成
 * dataUrl → 按 `src/core/types.ts` 的 Attachment 契约构造对象。
 * id 统一走 `src/core/ids.ts` 的生成器，避免各处自己拼 id。
 */

import { newId } from '../../core/ids';
import type { Attachment } from '../../core/types';
import {
  ATTACHMENT_ACCEPT,
  inferKind,
  validateFiles,
  type FileRejection,
  type FileLike,
} from './validate';

/** 选择结果：合法附件 + 拒绝原因 + 是否取消。*/
export interface PickResult {
  attachments: Attachment[];
  rejected: FileRejection[];
  /** 用户取消选择或未选任何文件。*/
  canceled: boolean;
}

export interface PickOptions {
  /** 已选中的数量，用于数量上限校验。*/
  existingCount?: number;
  /** 测试注入：替换默认的 FileReader 读取实现。*/
  read?: (file: File) => Promise<string>;
}

/** 读入结果：合法附件 + 拒绝原因。*/
export interface ReadAttachmentsResult {
  accepted: File[];
  rejected: FileRejection[];
  attachments: Attachment[];
}

/** 创建一个不显示的 file input（图片 / 音频 / 常见文件）。*/
export function createFileInput(): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = ATTACHMENT_ACCEPT;
  input.multiple = true;
  input.hidden = true;
  return input;
}

/** 用 FileReader 把文件读成 dataUrl。*/
export function readFileAsDataUrl(file: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') resolve(result);
      else reject(new Error('FileReader 未返回 dataUrl'));
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('读取文件失败'));
    };
    reader.readAsDataURL(file);
  });
}

/** 由文件与 dataUrl 构造符合 Attachment 契约的对象。*/
export function createAttachment(file: FileLike, dataUrl?: string): Attachment {
  return {
    id: newId(),
    kind: inferKind(file.type),
    name: file.name,
    mime: file.type,
    size: file.size,
    ...(dataUrl === undefined ? {} : { dataUrl }),
  };
}

/**
 * 校验 + 读入：合法文件变成 Attachment；超限 / 不支持的文件带中文原因返回，
 * 不影响同批其它合法文件。
 */
export async function filesToAttachments(
  files: readonly File[],
  options: PickOptions = {},
): Promise<ReadAttachmentsResult> {
  const { accepted, rejected } = validateFiles(files, options.existingCount ?? 0);
  const read = options.read ?? ((file: File) => readFileAsDataUrl(file));
  const attachments: Attachment[] = [];

  for (const file of accepted) {
    try {
      const dataUrl = await read(file);
      attachments.push(createAttachment(file, dataUrl));
    } catch {
      rejected.push({ file, reason: `读取失败：${file.name}` });
    }
  }

  return { accepted, rejected, attachments };
}

/** 触发一次系统文件选择，返回读入后的附件与拒绝原因。*/
export function pickAttachments(options: PickOptions = {}): Promise<PickResult> {
  const input = createFileInput();
  const parent = document.body ?? document.documentElement;
  parent.appendChild(input);

  return new Promise<PickResult>((resolve) => {
    let settled = false;
    const finish = (result: PickResult): void => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(result);
    };

    input.addEventListener(
      'change',
      () => {
        const selected = input.files === null ? [] : Array.from(input.files);
        if (selected.length === 0) {
          finish({ attachments: [], rejected: [], canceled: true });
          return;
        }
        void filesToAttachments(selected, options).then(
          ({ attachments, rejected }) => {
            finish({ attachments, rejected, canceled: false });
          },
          () => {
            finish({ attachments: [], rejected: [], canceled: true });
          },
        );
      },
      { once: true },
    );

    input.addEventListener(
      'cancel',
      () => {
        finish({ attachments: [], rejected: [], canceled: true });
      },
      { once: true },
    );

    input.click();
  });
}